"use client";

import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { AppImage as Image } from "@/components/ui/app-image";
import { blurHashToDataUrl } from "@/lib/media/blurhash";
import { watchVideoFrame } from "@/lib/media/video-frame";
import { cn } from "@/lib/utils";
import { HlsVideo, type HlsVideoProps } from "./hls-video";

type PosterVideoProps = Omit<HlsVideoProps, "poster" | "className"> & {
  posterUrl?: string | null;
  /** The asset's ThumbHash, painted until the poster itself has loaded. */
  blurHash?: string | null;
  /** `sizes` for the poster cover. */
  posterSizes?: string;
  /** Fetch the poster eagerly at high priority (the lead card only). */
  priority?: boolean;
  /** The frame the video and its cover fill. */
  className?: string;
  videoClassName?: string;
  /**
   * `object-fit` for the poster cover. Defaults to `object-cover`; profile
   * media passes `object-contain` so the poster and the first video frame
   * don't jump when a portrait/landscape clip replaces its cover.
   */
  posterClassName?: string;
  /**
   * The cover behind the poster (surface fill + ThumbHash). A letterboxed
   * viewer passes `bg-black bg-none` so the bands stay black.
   */
  coverClassName?: string;
};

const noFallback = () => null;

/**
 * A video that is never black: its poster sits ON TOP until the element has
 * painted a real frame, and comes back whenever that frame is lost.
 *
 * Every card, banner and feed video renders through this. The poster used to
 * be either the `<video poster>` attribute alone (event and class cards),
 * which iOS drops to black the moment `play()` is called, or an image BEHIND
 * a video revealed by a one-way `canplay` flag (hiive cards, banners), which
 * went black again every time the proximity scheduler released the player
 * and re-attached it on the way back. `watchVideoFrame` has the whole story.
 *
 * The cover is above the video rather than the video hidden under
 * `opacity: 0`, so the player is always composited normally and WebKit never
 * judges it invisible. The swap has no fade: the poster IS frame 0, so a
 * crossfade would only delay motion.
 */
export const PosterVideo = forwardRef<HTMLVideoElement, PosterVideoProps>(
  function PosterVideo(
    {
      posterUrl,
      blurHash,
      posterSizes = "(max-width: 768px) 90vw, 318px",
      priority = false,
      className,
      videoClassName,
      posterClassName = "object-cover",
      coverClassName,
      ...videoProps
    },
    ref,
  ) {
    const [hasFrame, setHasFrame] = useState(false);
    const element = useRef<HTMLVideoElement | null>(null);
    const attach = useCallback(
      (node: HTMLVideoElement | null) => {
        element.current = node;
        if (typeof ref === "function") return ref(node);
        if (ref) ref.current = node;
      },
      [ref],
    );

    // The <video> is the same element for this component's whole life; a new
    // `src` is a `loadstart`/`emptied`, which the watcher already treats as a
    // lost frame.
    useEffect(() => {
      const video = element.current;
      if (!video) return;
      return watchVideoFrame(video, setHasFrame);
    }, []);

    const placeholder = blurHashToDataUrl(blurHash);

    return (
      <div className={cn("relative h-full w-full overflow-hidden", className)}>
        <HlsVideo
          ref={attach}
          {...videoProps}
          data-has-poster={posterUrl ? "true" : undefined}
          className={cn(
            "absolute inset-0 h-full w-full object-cover",
            videoClassName,
          )}
        />
        <div
          aria-hidden
          data-slot="video-poster"
          className={cn(
            "pointer-events-none absolute inset-0 bg-surface-alt bg-cover bg-center",
            coverClassName,
            hasFrame && "invisible",
          )}
          style={
            placeholder ? { backgroundImage: `url(${placeholder})` } : undefined
          }
        >
          {posterUrl ? (
            <Image
              src={posterUrl}
              alt=""
              fill
              className={posterClassName}
              sizes={posterSizes}
              priority={priority}
              // The ThumbHash (or surface) behind it is the fallback; the
              // stock "image off" tile would read as a broken video.
              disableBlur
              fallback={noFallback}
            />
          ) : null}
        </div>
      </div>
    );
  },
);
